import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";
import { formatJsDate } from "../../utils/formatageDate";
import { AddDocumentModal } from "../../components/reusableComponents/AddDocumentModal";
import MyPDFViewer from "../../components/reusableComponents/PDFViewer";
import {
  Loader2,
  FileText,
  Download,
  Eye,
  Trash2,
  Plus,
  Upload,
  Calendar,
  User,
  Shield,
  GraduationCap,
  CreditCard,
  Building,
  Award,
  File,
  AlertCircle
} from "lucide-react";

const DocumentSection = ({ candidate }) => {
  const [documents, setDocuments] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [showDocument, setShowDocument] = useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      console.log("Chargement des documents pour le candidat:", candidate._id);
      const res = await axios.get(`/document/candidate/${candidate._id}`);
      console.log("Documents reçus:", res.data);

      setDocuments(
        res.data.map((document, idx) => ({
          ...document,
          key: idx,
        })),
      );

      if (res.data.length === 0) {
        console.log("Aucun document trouvé pour ce candidat");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des documents:", error);

      if (error.response?.status === 404) {
        console.log("Aucun document trouvé pour ce candidat");
        setDocuments([]);
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé aux documents");
        setDocuments([]);
      } else {
        toast.error("Erreur lors du chargement des documents");
        setDocuments([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getDocument = async (documentId) => {
    try {
      console.log("Récupération du document:", documentId);
      toast.info("Chargement du document...");

      const res = await axios.get(`/document/${documentId}`);
      console.log("Document reçu:", res.data);

      const { name, data } = res.data;
      const mimeType = name.split(".").pop().toLowerCase();

      setPreviewUrl(data);
      setDocumentType(mimeType);
      setShowDocument(true);

      toast.success("Document chargé avec succès");
    } catch (error) {
      console.error("Erreur lors de la récupération du document:", error);

      if (error.response?.status === 404) {
        toast.error("Document non trouvé");
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé à ce document");
      } else {
        toast.error("Erreur lors du chargement du document");
      }
    }
  };

  const downloadDocument = async (documentId, name) => {
    try {
      console.log("Téléchargement du document:", documentId, name);
      toast.info("Préparation du téléchargement...");

      const res = await axios.get(`/document/${documentId}`);
      const { data } = res.data;

      // Créer le blob et le lien de téléchargement
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Déterminer le type MIME basé sur l'extension
      const extension = name.split('.').pop().toLowerCase();
      let mimeType = 'application/octet-stream';

      switch (extension) {
        case 'pdf':
          mimeType = 'application/pdf';
          break;
        case 'jpg':
        case 'jpeg':
          mimeType = 'image/jpeg';
          break;
        case 'png':
          mimeType = 'image/png';
          break;
        case 'doc':
          mimeType = 'application/msword';
          break;
        case 'docx':
          mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          break;
        default:
          mimeType = 'application/octet-stream';
      }

      const blob = new Blob([byteArray], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${name} téléchargé avec succès`);
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);

      if (error.response?.status === 404) {
        toast.error("Document non trouvé");
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé à ce document");
      } else {
        toast.error("Erreur lors du téléchargement");
      }
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.")) {
      return;
    }

    try {
      console.log("Suppression du document:", documentId);
      await axios.delete(`/document/${documentId}`);
      toast.success("Document supprimé avec succès");
      loadDocuments(); // Recharger la liste
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);

      if (error.response?.status === 404) {
        toast.error("Document non trouvé");
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé pour supprimer ce document");
      } else {
        toast.error("Erreur lors de la suppression du document");
      }
    }
  };

  const getDocumentIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "cv":
        return <User className="w-5 h-5 text-blue-600" />;
      case "carte d'identité":
      case "passport":
        return <Shield className="w-5 h-5 text-green-600" />;
      case "diplôme":
      case "formation":
        return <GraduationCap className="w-5 h-5 text-purple-600" />;
      case "contrat":
        return <Building className="w-5 h-5 text-orange-600" />;
      case "attestation":
      case "certificat":
        return <Award className="w-5 h-5 text-yellow-600" />;
      default:
        return <File className="w-5 h-5 text-gray-600" />;
    }
  };

  const getDocumentTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "cv":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "carte d'identité":
      case "passport":
        return "bg-green-100 text-green-800 border-green-200";
      case "diplôme":
      case "formation":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "contrat":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "attestation":
      case "certificat":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';

    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isDocumentExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  const isDocumentExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    const today = new Date();
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  return (
    <div className="space-y-6">
      {showDocument && (
        <>
          {documentType === "pdf" ? (
            <MyPDFViewer
              base64={previewUrl}
              isOpen={showDocument}
              onClose={setShowDocument}
            />
          ) : (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <div className="bg-white p-4 rounded shadow-lg relative">
                <button
                  onClick={() => setShowDocument(false)}
                  className="absolute top-2 right-2 text-gray-600 hover:text-red-600"
                >
                  ✕
                </button>
                <img
                  src={`data:image/${documentType};base64,${previewUrl}`}
                  alt="document"
                  className="max-h-[80vh] max-w-[90vw]"
                />
              </div>
            </div>
          )}
        </>
      )}

      <AddDocumentModal
        candidate={candidate}
        isOpen={isAddDocumentModalOpen}
        documents={documents}
        setDocuments={setDocuments}
        onClose={() => setIsAddDocumentModalOpen(false)}
      />

      {/* Header avec statistiques */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
          <p className="text-gray-600 mt-1">
            Gestion des documents et pièces justificatives
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{documents.length}</div>
            <div className="text-sm text-gray-500">Documents</div>
          </div>
          <motion.button
            onClick={() => setIsAddDocumentModalOpen(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un document</span>
          </motion.button>
        </div>
      </div>

      {/* Liste des documents */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Chargement des documents...</p>
          </div>
        </div>
      ) : documents && documents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc, index) => (
            <motion.div
              key={doc._id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {/* Header du document */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    {getDocumentIcon(doc.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {doc.name}
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full border ${getDocumentTypeColor(doc.type)}`}>
                        {doc.type}
                      </span>

                      {/* Alertes d'expiration */}
                      {isDocumentExpired(doc.expirationDate) && (
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 border border-red-200">
                          Expiré
                        </span>
                      )}

                      {!isDocumentExpired(doc.expirationDate) && isDocumentExpiringSoon(doc.expirationDate) && (
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 border border-orange-200">
                          Expire bientôt
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Informations du document */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Ajouté le {formatJsDate(doc.createdAt)}</span>
                </div>

                {/* Informations spécifiques aux documents d'identité */}
                {doc.deliveryDate && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Shield className="w-4 h-4 mr-2" />
                    <span>Délivré le {formatJsDate(doc.deliveryDate)}</span>
                  </div>
                )}

                {doc.expirationDate && (
                  <div className="flex items-center text-sm text-gray-600">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    <span>Expire le {formatJsDate(doc.expirationDate)}</span>
                  </div>
                )}

                {doc.cardNumber && (
                  <div className="flex items-center text-sm text-gray-600">
                    <CreditCard className="w-4 h-4 mr-2" />
                    <span>N° {doc.cardNumber}</span>
                  </div>
                )}

                {/* Taille du fichier si disponible */}
                {(doc.size || doc.fileSize) && (
                  <div className="flex items-center text-sm text-gray-600">
                    <FileText className="w-4 h-4 mr-2" />
                    <span>
                      {typeof doc.size === 'number' ? formatFileSize(doc.size) :
                       typeof doc.fileSize === 'number' ? formatFileSize(doc.fileSize) :
                       doc.size || doc.fileSize}
                    </span>
                  </div>
                )}

                {/* Utilisateur qui a uploadé */}
                {doc.uploadedBy && (
                  <div className="flex items-center text-sm text-gray-600">
                    <User className="w-4 h-4 mr-2" />
                    <span>Par {doc.uploadedBy}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <motion.button
                  onClick={() => getDocument(doc._id)}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  title="Prévisualiser"
                >
                  <Eye className="w-4 h-4" />
                  <span>Voir</span>
                </motion.button>

                <motion.button
                  onClick={() => downloadDocument(doc._id, doc.name)}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  title="Télécharger"
                >
                  <Download className="w-4 h-4" />
                  <span>Télécharger</span>
                </motion.button>

                <motion.button
                  onClick={() => deleteDocument(doc._id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          className="text-center py-12 bg-gray-50 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun document
          </h3>
          <p className="text-gray-600 mb-4">
            Commencez par ajouter des documents pour ce candidat
          </p>
          <motion.button
            onClick={() => setIsAddDocumentModalOpen(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
          >
            Ajouter le premier document
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default DocumentSection;
